import React, { useEffect, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { IntlProvider } from "react-intl";
import { Toaster } from "react-hot-toast";
import TopBarProgress from "react-topbar-progress-indicator";
import SSRProvider from "react-bootstrap/SSRProvider";

import dynamic from "next/dynamic";
// const AnimatedCursor = dynamic(() => import("react-animated-cursor"), {
//   ssr: false,
// });

import "bootstrap/dist/css/bootstrap.css";
import "bootstrap/dist/css/bootstrap.rtl.min.css";
// import "aos/dist/aos.css";
import "swiper/css";
import "../styles/main.scss";

const languages = {
  ar: require("@/content/languages/ar.json"),
  en: require("@/content/languages/en.json"),
};

import { wrapper } from "../src/store";
import { useSelector } from "react-redux";
import { getSettings } from "../src/store/settings/actions";

const websiteSchemaByLocale = {
  ar: {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://designconcepts.sa/#website-ar",
    url: "https://designconcepts.sa/",
    name: "مفاهيم التصميم",
    inLanguage: "ar",
    publisher: {
      "@id": "https://designconcepts.sa/#organization",
    },
  },
  en: {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://designconcepts.sa/en#website-en",
    url: "https://designconcepts.sa/en",
    name: "Design Concepts",
    inLanguage: "en",
    publisher: {
      "@id": "https://designconcepts.sa/#organization",
    },
  },
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://designconcepts.sa/#organization",
  name: "Design Concepts",
  alternateName: "مفاهيم التصميم",
  url: "https://designconcepts.sa/",
  logo: "https://desginconcept.ejjadh.info/uploads/file-1767958980593.png",
  image: "https://desginconcept.ejjadh.info/uploads/file-1767958980593.png",
  telephone: "+966541302695",
  email: "info@designconcepts.sa",
  sameAs: [
    "https://www.facebook.com/designconcepts001",
    "https://x.com/d_c_sa",
    "https://www.instagram.com/d_c_sa/",
    "https://www.snapchat.com/@designcon2016",
    "https://www.tiktok.com/@d_c_sa",
    "https://maps.app.goo.gl/vSVLBkVYeBKecNEQA",
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Riyadh",
    addressCountry: "SA",
  },
};

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const { locale, defaultLocale, asPath } = router;
  const cleanPath = (asPath || "/").split("?")[0].split("#")[0] || "/";
  const normalizedPath =
    locale === "en"
      ? cleanPath.replace(/^\/en(?=\/|$)/, "") || "/"
      : cleanPath.replace(/^\/ar(?=\/|$)/, "") || "/";
  const pathSuffix = normalizedPath === "/" ? "" : normalizedPath;
  const arUrl = `https://designconcepts.sa${pathSuffix}`;
  const enUrl = `https://designconcepts.sa/en${pathSuffix}`;
  const websiteSchema =
    locale === "en" ? websiteSchemaByLocale.en : websiteSchemaByLocale.ar;
  const messages = languages[locale];
  const dir = locale === "ar" ? "rtl" : "ltr";

  const [Progress, setProgress] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [showCursor, setShowCursor] = useState(false);
  const [canRunMotionEnhancements, setCanRunMotionEnhancements] =
    useState(false);

  const { settings } = useSelector((state) => state.settings);

  useEffect(() => {
    TopBarProgress.config({
      barThickness: 3,
      barColors: {
        0: "#fff",
        0.5: "#000",
        1.0: "#000",
      },
    });
  }, []);

  useEffect(() => {
    const handleRouteChangeStart = () => setProgress(true);
    const handleRouteChangeDone = () => setProgress(false);

    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeComplete", handleRouteChangeDone);
    router.events.on("routeChangeError", handleRouteChangeDone);

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeComplete", handleRouteChangeDone);
      router.events.off("routeChangeError", handleRouteChangeDone);
    };
  }, [router]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const saveData = Boolean(navigator?.connection?.saveData);
    const lowCpuCores =
      typeof navigator?.hardwareConcurrency === "number" &&
      navigator.hardwareConcurrency <= 4;
    const lowDeviceMemory =
      typeof navigator?.deviceMemory === "number" &&
      navigator.deviceMemory <= 4;

    setCanRunMotionEnhancements(
      !prefersReducedMotion && !saveData && !lowCpuCores && !lowDeviceMemory,
    );
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const updateDesktopState = () => setIsDesktop(mediaQuery.matches);
    updateDesktopState();

    mediaQuery.addEventListener("change", updateDesktopState);

    return () => {
      mediaQuery.removeEventListener("change", updateDesktopState);
    };
  }, []);

  useEffect(() => {
    if (
      !canRunMotionEnhancements ||
      !isDesktop ||
      typeof window === "undefined"
    ) {
      setShowCursor(false);
      return;
    }

    let cancelled = false;
    let idleId;
    let timerId;
    let hasStarted = false;

    const enableCursor = () => {
      if (cancelled) return;
      setShowCursor(true);
    };

    const scheduleCursor = () => {
      if (hasStarted) return;
      hasStarted = true;

      if (window.requestIdleCallback) {
        idleId = window.requestIdleCallback(enableCursor, { timeout: 5000 });
      } else {
        timerId = window.setTimeout(enableCursor, 3500);
      }
    };

    const handleFirstPointerMove = () => {
      scheduleCursor();
    };

    window.addEventListener("pointermove", handleFirstPointerMove, {
      once: true,
      passive: true,
    });

    return () => {
      cancelled = true;
      window.removeEventListener("pointermove", handleFirstPointerMove);
      if (idleId && window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId);
      }
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [isDesktop, canRunMotionEnhancements]);

  useEffect(() => {
    const isProduction = process.env.NODE_ENV === "production";
    const isDevGtmEnabled = process.env.NEXT_PUBLIC_ENABLE_GTM_DEV === "true";
    const isLocalhost =
      typeof window !== "undefined" && window.location.hostname === "localhost";

    if (!isProduction && !isDevGtmEnabled && !isLocalhost) return;

    const gtmId = settings?.googleId || settings?.googleTagManager;
    const gtmScriptHtml = settings?.googleTagManager?.includes("gtm.js")
      ? settings.googleTagManager
      : gtmId
        ? `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`
        : "";
    const gtmNoScriptHtml = settings?.googleNoScript?.includes("iframe")
      ? settings.googleNoScript
      : gtmId
        ? `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
              height="0" width="0" style="display:none;visibility:hidden"></iframe>`
        : "";

    if (gtmScriptHtml && !document.getElementById("gtm-script")) {
      const script = document.createElement("script");
      script.id = "gtm-script";
      script.innerHTML = gtmScriptHtml;
      document?.head?.appendChild(script);
    }

    if (gtmNoScriptHtml && !document.getElementById("gtm-noscript")) {
      const noscript = document.createElement("noscript");
      noscript.id = "gtm-noscript";
      noscript.innerHTML = gtmNoScriptHtml;
      document.body.appendChild(noscript);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      page_path: router.pathname,
    });

    const handleRouteChange = (url) => {
      if (!window.dataLayer) return;
      window.dataLayer.push({
        event: "page_view",
        page_path: url,
      });
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [
    router.events,
    router.pathname,
    settings?.googleId,
    settings?.googleTagManager,
    settings?.googleNoScript,
  ]);

  useEffect(() => {
    (async () => {
      // const AOS = await import("aos"); // استيراد ديناميكي داخل الـ client فقط
      // AOS.init({
      //   // once: true,
      //   // disable: "mobile",
      // });
    })();

    document.documentElement.dir = dir;
    document.body.style.direction = dir;
    document.body.setAttribute("dir", dir);

    let rafId;
    let idleId;
    let timerId;
    let loadTimerId;
    let resizeObserver;
    let lenis;
    let disposed = false;
    let lenisCleanup = null;
    let bootstrapped = false;

    const initLenis = async () => {
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

      if (!canRunMotionEnhancements || !isDesktop || disposed) return;

      const { default: Lenis } = await import("@studio-freight/lenis");

      if (disposed) return;

      lenis = new Lenis({
        duration: 1.3,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        smoothTouch: false,
        lerp: 0.12,
        wheelMultiplier: 1,
        touchMultiplier: 1.5,
      });

      const handleWindowResize = () => lenis?.resize();
      const handleImageLoad = () => {
        setTimeout(() => {
          lenis?.resize();
        }, 100);
      };

      resizeObserver = new ResizeObserver(() => {
        lenis?.resize();
      });

      resizeObserver.observe(document.body);
      window.addEventListener("resize", handleWindowResize);
      window.addEventListener("load", handleWindowResize);
      document.addEventListener("load", handleImageLoad, true);

      const raf = (time) => {
        lenis?.raf(time);
        rafId = requestAnimationFrame(raf);
      };

      rafId = requestAnimationFrame(raf);

      lenisCleanup = () => {
        if (rafId) cancelAnimationFrame(rafId);
        resizeObserver?.disconnect();
        window.removeEventListener("resize", handleWindowResize);
        window.removeEventListener("load", handleWindowResize);
        document.removeEventListener("load", handleImageLoad, true);
        lenis?.destroy();
      };
    };

    const scheduleLenis = () => {
      if (bootstrapped || disposed) return;
      bootstrapped = true;

      if (window.requestIdleCallback) {
        idleId = window.requestIdleCallback(initLenis, { timeout: 3500 });
      } else {
        timerId = window.setTimeout(initLenis, 2500);
      }
    };

    const handleFirstInteraction = () => {
      scheduleLenis();
    };

    const registerInteractionListeners = () => {
      window.addEventListener("wheel", handleFirstInteraction, {
        once: true,
        passive: true,
      });
      window.addEventListener("touchstart", handleFirstInteraction, {
        once: true,
        passive: true,
      });
      window.addEventListener("keydown", handleFirstInteraction, {
        once: true,
      });
    };

    if (!canRunMotionEnhancements) {
      return () => {
        disposed = true;
      };
    }

    if (document.readyState === "complete") {
      registerInteractionListeners();
      loadTimerId = window.setTimeout(scheduleLenis, 4500);
    } else {
      window.addEventListener(
        "load",
        () => {
          registerInteractionListeners();
          loadTimerId = window.setTimeout(scheduleLenis, 4500);
        },
        { once: true },
      );
    }

    return () => {
      disposed = true;
      window.removeEventListener("wheel", handleFirstInteraction);
      window.removeEventListener("touchstart", handleFirstInteraction);
      window.removeEventListener("keydown", handleFirstInteraction);
      if (idleId && window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId);
      }
      if (timerId) {
        window.clearTimeout(timerId);
      }
      if (loadTimerId) {
        window.clearTimeout(loadTimerId);
      }
      if (lenisCleanup) {
        lenisCleanup();
      }
    };
  }, [dir, canRunMotionEnhancements]);

  return (
    <>
      <Head>
        <title>{settings?.appName?.[locale]}</title>
        <link
          rel="shortcut icon"
          href={settings?.favicon ? settings?.favicon : "/favicon.png"}
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        <link
          rel="preconnect"
          href="https://designconcepts.sa"
          crossOrigin="anonymous"
        />
        <link rel="alternate" href={arUrl} hrefLang="ar" />
        <link rel="alternate" href={enUrl} hrefLang="en" />
        <link rel="alternate" href={arUrl} hrefLang="x-default" />
        <link rel="canonical" href={locale === "ar" ? arUrl : enUrl} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(websiteSchema),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(organizationSchema),
          }}
        />
      </Head>

      {Progress && <TopBarProgress />}
      {/* {isDesktop && showCursor && (
        <AnimatedCursor
          innerSize={8}
          outerSize={40}
          color="0,0,0"
          outerAlpha={0.3}
          innerScale={1}
          outerScale={2}
          trailingSpeed={1}
          showSystemCursor={false}
          clickables={[
            "a",
            "button",
            "input",
            "textarea",
            "select",
            ".link",
            "[role='button']",
          ]}
        />
      )} */}
      <IntlProvider
        messages={messages}
        defaultLocale={defaultLocale}
        locale={locale}
      >
        <SSRProvider>
          <div className="wrap">
            <Component {...pageProps} />
            <Toaster position="top-left" reverseOrder={false} />
          </div>
        </SSRProvider>
      </IntlProvider>
    </>
  );
}

MyApp.getInitialProps = wrapper.getInitialAppProps((store) => async () => {
  store.dispatch(
    getSettings({
      cookies: {},
    }),
  );
  return {
    pageProps: {},
  };
});

export default wrapper.withRedux(MyApp);
